import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, TableRow, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TableCell from 'src/components/Table/TableCell';
import { getClientFullName } from 'src/utils/common';
import { useTranslation } from 'react-i18next';
import { getContractEventsLabels } from 'src/utils/constants/contracts';
import selectFormatDate from 'src/utils/selectDateFormat';
import { getEventContractStatusIcon } from 'src/utils/contracts';
import OverflowTipography from 'src/components/OverflowTipography';
import { Link } from 'react-router-dom';
import { IconStatusAndColor } from 'src/utils/constants/common';
import SettlementComparisonDialog from './SettlementComparisonDialog';

// The whole difference is produced by the grace period: while the client pays
// nothing the fiscal balance does not amortize but the accounting book does.
// A contract with no grace period closes both balances on the same amount, so
// the API reports a difference of zero give or take rounding drift — nothing
// to compare and nothing to book. In that case the row shows no icon at all
// and the dialog is unreachable, rather than offering a breakdown of $0.00.
// One peso sits below any adjustment worth recording.
const MIN_REPORTABLE_DIFFERENCE = 1;

const hasReportableSettlement = settlement =>
  !!settlement &&
  Math.abs(settlement.difference ?? 0) >= MIN_REPORTABLE_DIFFERENCE;

const ContractEventsRow = ({
  eventType,
  createdAt,
  status,
  client,
  settlement
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const showSettlement = hasReportableSettlement(settlement);

  const clientName = useMemo(() => {
    if (!client) return t('COMMON.NO_DATA');

    return getClientFullName(client);
  }, [client, t]);
  const statusIcon = useMemo(() => getEventContractStatusIcon(status), [
    status
  ]);
  const eventLabels = getContractEventsLabels(t);
  const eventLabel =
    eventLabels.find(label => label.key === eventType)?.name || eventType;

  const handleOpenDialog = useCallback(() => setDialogOpen(true), []);
  const handleCloseDialog = useCallback(() => setDialogOpen(false), []);

  return (
    <>
      <TableRow hover>
        <TableCell align="center">{selectFormatDate(createdAt)}</TableCell>
        <TableCell
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          <OverflowTipography title={clientName}>
            {client ? (
              <Link
                style={{ textDecoration: 'none' }}
                to={`/clients/detail/${client.id}`}
                maxWidth="200px"
              >
                {clientName}
              </Link>
            ) : (
              t('COMMON.NO_DATA')
            )}
          </OverflowTipography>
        </TableCell>

        <TableCell align="center">{statusIcon}</TableCell>
        <TableCell align="center">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
          >
            {eventLabel}
            {showSettlement && (
              <Tooltip title={t('CONTRACT_VIEW.EVENTS.SETTLEMENT.SEE_DETAIL')}>
                <IconButton
                  size="small"
                  aria-label={t('CONTRACT_VIEW.EVENTS.SETTLEMENT.SEE_DETAIL')}
                  onClick={handleOpenDialog}
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    style={{ color: IconStatusAndColor.WARNING.color }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
      <SettlementComparisonDialog
        open={dialogOpen && showSettlement}
        onClose={handleCloseDialog}
        settlement={settlement}
        eventDate={createdAt}
      />
    </>
  );
};

ContractEventsRow.propTypes = {
  eventType: PropTypes.string,
  createdAt: PropTypes.string,
  status: PropTypes.string,
  client: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    fullName: PropTypes.string,
    personType: PropTypes.string,
    paternalName: PropTypes.string,
    maternalName: PropTypes.string,
    denomination: PropTypes.string
  }),
  // Only present on a completed early termination; the API omits it otherwise.
  settlement: PropTypes.shape({
    paidAmount: PropTypes.number,
    accountingBalance: PropTypes.number,
    difference: PropTypes.number
  })
};

export default ContractEventsRow;
